import {
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useMemo } from 'react';
import { matchPath, useLocation, useMatch } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useCommands } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from '../../commandLauncher.module.scss';
import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

/** Represents the main actions list of the command launcher. */
export const MainSelectionList = (): JSX.Element | null => {
  const { pathname } = useLocation();
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { keybindings } = useCommands();

  // Match both /conversation/:csid and /conversation/:csid/activity-log
  const conversationRouteMatch = useMatch('/conversation/:csid/*');
  const isOnConversationPage = conversationRouteMatch !== null;

  const {
    actions: allActions,
    query,
    scope,
    isActive,
    createAction,
    performAction,
    setQuery,
  } = useCommandLauncher();

  // Assume the query might be a conversation ID if it consists of 1 to 7 digits.
  const isMaybeConversationId = /^\d{1,7}$/.test(query);

  const actions = useMemo(() => {
    const { all, current } = allActions
      .filter(
        (action) =>
          !action.scope || action.scope?.includes(CommandLauncherScope.Main),
      )
      .reduce<{
        all: readonly CommandLauncherAction[];
        current: readonly CommandLauncherAction[];
      }>(
        (actions, currentAction) => {
          if (currentAction.routes) {
            // Use matchPath to support parameterized routes like /conversation/:csid
            const matchesRoute = currentAction.routes.some((route) =>
              matchPath(route, pathname),
            );
            if (matchesRoute) {
              return {
                ...actions,
                current: [...actions.current, currentAction],
              };
            }
            return actions;
          }

          return { ...actions, all: [...actions.all, currentAction] };
        },
        { all: [], current: [] },
      );

    const combinedActions = [...current, ...all];

    // When on a conversation page, prioritize "Conversation" section actions
    if (isOnConversationPage) {
      return combinedActions.sort((a, b) => {
        const aIsConversation = a.section === 'Conversation';
        const bIsConversation = b.section === 'Conversation';
        if (aIsConversation && !bIsConversation) return -1;
        if (!aIsConversation && bIsConversation) return 1;
        return 0;
      });
    }

    return combinedActions;
  }, [allActions, pathname, isOnConversationPage]);

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandLauncherAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    const action = event.selectedItem.value;
    performAction(action);

    // Only close the modal if the action doesn't want to stay open
    if (!action.keepOpen) {
      dispatch(appActions.hideModal({ type: 'app:commandLauncher' }));
    }
  };

  const { listActions, hasMatchingResults } = useMemo(() => {
    let results: CommandLauncherAction[] = [];
    let matchCount = 0;

    if (query) {
      const fuse = new Fuse(actions, {
        keys: ['label', 'aliases'],
        threshold: 0.2,
      });

      results = fuse.search(query).map(({ item }) => item);
      matchCount = results.length;

      // If the query is a plain number between 1-7 digits, treat it as a potential conversation ID
      // and add a special action to open that conversation
      if (isMaybeConversationId) {
        results.push(
          createAction({
            label: `Open conversation #${query}`,
            icon: 'chat',
            section: 'Actions',
            performFn: () =>
              navigate({
                path: '/conversation/:csid',
                params: { path: { csid: query } },
              }),
          }),
        );
      }

      // Add fallback search action when there's a query
      results.push(
        createAction({
          label: `Find conversations matching "${query}"`,
          icon: 'search',
          section: 'Actions',
          keepOpen: true,
          performFn: () =>
            setQuery(`${CommandLauncherScope.Conversation}${query}`),
        }),
      );
    } else {
      results = [...actions];
      matchCount = results.length;
    }

    return { listActions: results, hasMatchingResults: matchCount > 0 };
  }, [actions, createAction, navigate, query, isMaybeConversationId, setQuery]);

  if (scope !== CommandLauncherScope.Main) {
    return null;
  }

  // Show empty state when query exists but no commands match
  const showEmptyState = query && !isMaybeConversationId && !hasMatchingResults;

  return (
    <>
      {showEmptyState && (
        <div className={styles.emptyState}>
          <span>No commands found for "{query}"</span>

          <span className={styles.emptyStateHint}>
            Press <kbd>↵</kbd> to search
          </span>
        </div>
      )}

      <SelectionList<CommandLauncherAction>
        isActive={isActive}
        onChange={handleSelectionListOnChange}
      >
        {getCommandLauncherActionItems(listActions, keybindings)}
      </SelectionList>
    </>
  );
};
